import { OktaAuthOptions } from '@okta/okta-auth-js';
import { AppInsights, Ciam, FaroConfig, IEnvironment, StandaloneType } from './environment.type';
import { Env } from '@app/shared/utils/enums/environment.enum';

class Environment implements IEnvironment {
  get envName(): Env {
    return import.meta.env.VETSERVICES_ENV_NAME as Env;
  }

  get production(): boolean {
    return import.meta.env.VETSERVICES_PRODUCTION === 'true';
  }

  get forceEnableFeatures(): boolean {
    return import.meta.env.VETSERVICES_FORCE_ENABLE_FEATURES === 'true';
  }

  get isStandalone(): boolean {
    return import.meta.env.VETSERVICES_IS_STANDALONE === 'true';
  }

  get standaloneType(): StandaloneType {
    return import.meta.env.VETSERVICES_STANDALONE_TYPE as StandaloneType;
  }

  get isShowSendBackDataButton(): boolean {
    return import.meta.env.VETSERVICES_IS_SHOW_SEND_BACK_DATA_BUTTON === 'true';
  }

  get graphColor(): string {
    return import.meta.env.VETSERVICES_GRAPH_COLOR;
  }

  get scriptEnv(): string {
    return import.meta.env.VETSERVICES_SCRIPT_ENV;
  }

  get apiUrl(): string {
    return import.meta.env.VETSERVICES_API_URL;
  }

  get bffApiUrl(): string {
    return import.meta.env.VETSERVICES_BFF_API_URL;
  }

  get productApiUrl(): string {
    return import.meta.env.VETSERVICES_PRODUCT_API_URL;
  }

  get srsProductApiUrl(): string {
    return import.meta.env.VETSERVICES_SRS_PRODUCT_API_URL;
  }

  get vetApiUrl(): string {
    return import.meta.env.VETSERVICES_VET_API_URL;
  }

  get clinicApiUrl(): string {
    return import.meta.env.VETSERVICES_CLINIC_API_URL;
  }

  get toolApiUrl(): string {
    return import.meta.env.VETSERVICES_TOOL_API_URL;
  }

  get nutritionApiUrl(): string {
    return import.meta.env.VETSERVICES_NUTRITION_API_URL;
  }

  get healthNutritionApiUrl(): string {
    return import.meta.env.VETSERVICES_HEALTH_NUTRITION_API_URL;
  }

  get petOwnersApiUrl(): string {
    return import.meta.env.VETSERVICES_PET_OWNERS_API_URL;
  }

  get coreApiUrl(): string {
    return import.meta.env.VETSERVICES_CORE_API_URL;
  }

  get myAccountUrl(): string {
    return import.meta.env.VETSERVICES_MY_ACCOUNT_URL;
  }

  get sendBackDataApiUrl(): string | undefined {
    return import.meta.env.VETSERVICES_SEND_BACK_DATA_API_URL;
  }

  get blobUrl(): string {
    return import.meta.env.VETSERVICES_BLOB_URL;
  }

  get blobQueryParams(): string {
    return import.meta.env.VETSERVICES_BLOB_QUERY_PARAMS;
  }

  get blobApiUrl(): string {
    return import.meta.env.VETSERVICES_BLOB_API_URL;
  }

  get apimSubscriptionKey(): string {
    return import.meta.env.VETSERVICES_APIM_SUBSCRIPTION_KEY;
  }

  get marsFooterScript(): string {
    return import.meta.env.VETSERVICES_MARS_FOOTER_SCRIPT;
  }

  get oneTrustId(): string {
    return import.meta.env.VETSERVICES_ONETRUST_ID;
  }

  get fgsUrl(): string | undefined {
    return import.meta.env.VETSERVICES_FGS_URL;
  }

  get individualisUrl(): string {
    return import.meta.env.VETSERVICES_INDIVIDUALIS_URL;
  }

  get appInsights(): AppInsights {
    return {
      instrumentationKey: import.meta.env.VETSERVICES_APP_INSIGHTS_INSTRUMENTATION_KEY,
      role: import.meta.env.VETSERVICES_APP_INSIGHTS_ROLE,
      roleInstance: import.meta.env.VETSERVICES_APP_INSIGHTS_ROLE_INSTANCE,
    };
  }

  get faroConfig(): FaroConfig {
    return {
      enabled: import.meta.env.VETSERVICES_FARO_ENABLED === 'true',
      url: import.meta.env.VETSERVICES_FARO_URL,
      apiKey: import.meta.env.VETSERVICES_FARO_API_KEY,
    };
  }

  get oktaConfig(): OktaAuthOptions {
    return {
      issuer: import.meta.env.VETSERVICES_OKTA_ISSUER,
      clientId: import.meta.env.VETSERVICES_OKTA_CLIENT_ID,
      redirectUri: import.meta.env.VETSERVICES_OKTA_REDIRECT_URI,
      postLogoutRedirectUri: import.meta.env.VETSERVICES_OKTA_POST_LOGOUT_REDIRECT_URI,
      pkce: import.meta.env.VETSERVICES_OKTA_PKCE === 'true',
      maxClockSkew: JSON.parse(import.meta.env.VETSERVICES_OKTA_MAX_CLOCK_SKEW),
      tokenManager: {
        autoRenew: import.meta.env.VETSERVICES_OKTA_TOKEN_MANAGER_AUTO_RENEW === 'true',
        autoRemove: import.meta.env.VETSERVICES_OKTA_TOKEN_MANAGER_AUTO_REMOVE === 'true',
      },
      scopes: JSON.parse(import.meta.env.VETSERVICES_OKTA_SCOPES),
    };
  }

  get ciam(): Ciam {
    return {
      apiUrl: import.meta.env.VETSERVICES_CIAM_API_URL,
      app: import.meta.env.VETSERVICES_CIAM_APP_URL,
      redirectCallback: import.meta.env.VETSERVICES_CIAM_REDIRECT_CALLBACK,
    };
  }
}

export const environment = new Environment();
